/* eslint-disable no-param-reassign */
import React, { useEffect, useRef } from 'react';

import classNames from 'classnames';
import { NavLink } from 'react-router-dom';
import Flatpickr from 'react-flatpickr';
import moment from 'moment-timezone';

import Tooltip from '../../Tooltip';

import './style.css';

function Datepicker({
  align,
  label,
  value,
  name,
  setFieldValue,
  tabindex,
  isVisible,
  error,
  touched,
  readOnly,
  disablePastDates,
  link,
  onReadOnlyClick,
  tooltip,
}) {
  const inputRef = useRef();

  const options = {
    disable: disablePastDates
      ? [
          (date) => {
            return (
              !moment(date).isSameOrAfter(moment().startOf('day')) &&
              moment(date).toISOString() !== moment(value).toISOString()
            );
          },
        ]
      : [],
    disableMobile: true,
    static: true,
    monthSelectorType: 'static',
    value,
    dateFormat: 'M j, Y',
    // defaultDate: [new Date().setDate(new Date().getDate() - 6), new Date()],
    prevArrow:
      '<svg class="fill-current" width="7" height="11" viewBox="0 0 7 11"><path d="M5.4 10.8l1.4-1.4-4-4 4-4L5.4 0 0 5.4z" /></svg>',
    nextArrow:
      '<svg class="fill-current" width="7" height="11" viewBox="0 0 7 11"><path d="M1.4 10.8L0 9.4l4-4-4-4L1.4 0l5.4 5.4z" /></svg>',
    onReady: (selectedDates, dateStr, instance) => {
      instance.element.value = dateStr.replace('to', '-');
      const customClass = align || '';
      instance.calendarContainer.classList.add(`flatpickr-${customClass}`);
    },
    onChange: ([date]) => {
      //  date.setHours(0, 0, 0, 0);
      // const formattedDate = date.toISOString().split('T')[0];

      setFieldValue(name, moment(date).tz('America/Chicago').toDate());
    },
    errorHandler: (err) => {
      // Prevent errorHandler from spamming console with invalid date error
      // It is not causing any real issues and therefore can be ignored
      if (err.message !== 'Invalid date provided: Invalid Date') {
        console.warn(err);
      }
    },
  };

  // Focus on input if tabindex = 1
  useEffect(() => {
    if (tabindex === 1) {
      inputRef.current?.flatpickr?.input?.click();
      inputRef.current?.flatpickr?.input?.focus();
    }
  }, [tabindex, isVisible]);

  return (
    <div className="relative w-full datePickerContainer">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="block text-sm font-medium">{label}</h3>
        {tooltip && <Tooltip size="md">{tooltip}</Tooltip>}
      </div>
      {readOnly ? (
        <div
          className={classNames(
            'form-input font-medium w-full h-[37px] border-0 shadow-none',
            {
              'cursor-pointer underline text-blue-500 dark:text-blue-400 dark:hover:text-blue-500':
                onReadOnlyClick,
              'dark:bg-slate-800 text-slate-500 readOnly, dark:text-slate-300 dark:hover:text-slate-200':
                !onReadOnlyClick,
            }
          )}
          onClick={onReadOnlyClick}
        >
          {value
            ? moment(value).tz('America/Chicago').format('MMM D, YYYY')
            : ''}
        </div>
      ) : (
        <Flatpickr
          className="form-input pl-9 dark:bg-slate-800 text-slate-500 hover:text-slate-600 dark:text-slate-300 dark:hover:text-slate-200 font-medium w-full"
          options={options}
          value={moment(value).startOf('day').toDate()}
          tabIndex={tabindex}
          ref={inputRef}
        />
      )}
      {!readOnly && (
        <div className="absolute inset-0 right-auto flex items-center pointer-events-none">
          <svg
            className="w-4 h-4 fill-current text-slate-500 dark:text-slate-400 ml-3 absolute top-9"
            viewBox="0 0 16 16"
          >
            <path d="M15 2h-2V0h-2v2H9V0H7v2H5V0H3v2H1a1 1 0 00-1 1v12a1 1 0 001 1h14a1 1 0 001-1V3a1 1 0 00-1-1zm-1 12H2V6h12v8z" />
          </svg>
        </div>
      )}
      {link && (
        <NavLink
          className="inline-block w-full text-sm underline hover:no-underline text-right"
          to={link.url}
        >
          {link.title}
        </NavLink>
      )}
      {error && touched && (
        <div className="text-red-500 text-sm mt-1">{error}</div>
      )}
    </div>
  );
}

export default Datepicker;
